import React, { useState } from 'react';
import Joyride from 'react-joyride';

import { Tabs } from 'components/ui';
import { AddPatientIcon, CircleQuestionIcon } from 'assets/icons';
import { AppRoutes } from 'routes/app-routes';
import { useNavigate } from 'react-router-dom';
import { BaseButton } from 'components/ui/buttons';
import { useAuth } from 'contexts/AuthContextProvider';
import { Tooltip } from 'components/roster/TooltipTour';
import { Header } from 'components/details-pages/Header';
import { rosterTour, TourStepStatus } from 'types/tours';
import { PatientList } from 'components/roster/PatientList';
import { SearchInputField } from 'components/ui/form-fields';
import { SortBySelector } from 'components/ui/SortBySelector';
import { StartTourModal } from 'components/roster/StartTourModal';
import { LocalStorageService } from 'services/localStorageService';
import { rosterTourSteps } from 'components/roster/rosterTourSteps';
import { rosterSortByOptions, usePatientsData } from 'hooks/usePatientsData';
import { Switch } from 'components/ui/Switch';
import { Footer } from 'components/details-pages/Footer';

const MY_PATIENTS_TAB_INDEX = 0;

export const Roster: React.FC = () => {
  const tabTitles = ['My patients', 'All patients'];

  const [isShowTourModal, setIsShowTourModal] = useState<boolean>(
    LocalStorageService.getLocalStorage(rosterTour) === null,
  );
  const [isStartTour, setIsStartTour] = useState<boolean>(false);
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const [isGrouped, setIsGrouped] = useState<boolean>(false);

  const {
    setSearchValue,
    searchValue,
    sortBy,
    arePatientsEmpty,
    setSortBy,
    activePatients,
    activePatientsGrouped,
    notActivePatients,
    practitioners,
    fetch,
    submitting,
  } = usePatientsData(activeTabIndex === MY_PATIENTS_TAB_INDEX);

  const handleTabChange = async (index: number) => {
    setIsGrouped(false);
    setActiveTabIndex(index);
  };

  const restartTour = () => {
    setIsStartTour(false);
    setIsShowTourModal(true);
    LocalStorageService.removeLocalStorage(rosterTour);
  };

  const navigate = useNavigate();
  const { user, isLoading } = useAuth();

  const steps = rosterTourSteps.filter(step => document.querySelector(step.target));

  const startTour = () => {
    setIsShowTourModal(false);
    setIsStartTour(true);
  };

  const skippedTour = () => {
    setIsStartTour(false);
    setIsShowTourModal(false);
    LocalStorageService.setLocalStorage(rosterTour, TourStepStatus.SKIPPED);
  };

  const patientListProps = {
    activePatients,
    activePatientsGrouped,
    notActivePatients,
    practitioners,
    search: searchValue,
    fetch: () => fetch(activeTabIndex === MY_PATIENTS_TAB_INDEX),
    isPatientsEmpty: arePatientsEmpty,
    isSubmitting: submitting,
    isGrouped,
  };

  return (
    <div className="flex flex-col content-between  min-h-screen">
      {isLoading ? (
        <div>Loading...</div>
      ) : (
        <>
          <Header />
          <div className="flex flex-col content-between w-full max-w-[969px] mx-auto">
            <div className="py-6 px-4 sm:px-10">
              <div className="relative grid grid-cols-1 sm:grid-cols-8 sm:justify-between sm:items-center p-6 sm:p-2 md:p-4 bg-sea-200 sm:bg-add-patient bg-center bg-no-repeat dark:sm:bg-add-patient-dark dark:sm:bg-eastern-blue w-full h-[180px] rounded-2xl">
                <span
                  onClick={restartTour}
                  className="absolute flex items-center gap-1 top-2 right-2 md:top-4 md:right-4 text-xs text-gray-500 cursor-pointer"
                >
                  <CircleQuestionIcon className="w-4 h-4 text-gray-500" />
                  Restart tour
                </span>
                <div className="flex flex-row gap-2 lg:gap-4 col-span-3">
                  <div className="pb-[21px] sm:pb-0">
                    <h2 className="text-indigo text-[28px] font-semibold leading-[normal]">Hi, {user?.name}</h2>
                    <p className="text-indigo text-sm font-normal leading-[130%]">Track and assess your patients</p>
                  </div>
                </div>
                <div className="col-span-2"></div>
                <div className="col-span-3">
                  <BaseButton
                    className="add-patient w-full sm:w-auto ml-auto"
                    scheme="cta-primary"
                    size="xl"
                    icon={AddPatientIcon}
                    isIconOnLeftSide
                    onClick={() => navigate(AppRoutes.Onboarding)}
                  >
                    Add patient
                  </BaseButton>
                </div>
              </div>

              <div className="flex w-full flex-col mt-6 items-start bg-white-400 shadow-section rounded-2xl overflow-hidden">
                <Tabs
                  titles={tabTitles}
                  contents={[<PatientList {...patientListProps} />, <PatientList {...patientListProps} />]}
                  activeTab={activeTabIndex}
                  setActiveTab={handleTabChange}
                  additionalControls={
                    <div className="flex flex-col lg:flex-row mt-5 md:mt-0 px-4 gap-x-4 gap-y-1">
                      {activeTabIndex === 1 && (
                        <Switch label="Group: " checked={isGrouped} onChange={() => setIsGrouped(x => !x)} />
                      )}
                      <SortBySelector
                        className="min-w-40 mx-2"
                        options={rosterSortByOptions}
                        value={sortBy}
                        onChange={setSortBy}
                      />
                    </div>
                  }
                  searchSlot={
                    <SearchInputField
                      value={searchValue}
                      onChange={event => setSearchValue(event.target.value)}
                      clearSearchInput={() => setSearchValue('')}
                      placeholder="Search for patient"
                    />
                  }
                />
              </div>
            </div>
            <Footer />
          </div>

          <Joyride
            steps={steps}
            run={isStartTour}
            spotlightPadding={2}
            styles={{
              spotlight: {
                backgroundColor: 'rgba(255, 255, 255, 0.4)',
              },
            }}
            tooltipComponent={props => (
              <Tooltip {...props} tourName={rosterTour} stepsCount={steps.length} skipTour={skippedTour} />
            )}
          />
          {isShowTourModal && (
            <StartTourModal
              title="This is your patient roster page."
              description="Since it's your first log-in, we'll show you around."
              onStartTourClick={startTour}
              onCancelClick={skippedTour}
            />
          )}
        </>
      )}
    </div>
  );
};
